import React, { useState } from 'react';
import { Image } from 'react-bootstrap';
import { ImageIcon, AlertCircle } from 'lucide-react';

/**
 * Composant pour afficher les images de produits avec gestion d'erreurs améliorée
 */
const ProductImage = ({ 
  product, 
  size = 'medium', 
  className = '', 
  style = {},
  showPlaceholder = true,
  alt = null
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  /**
   * ✅ CORRECTION : Fonction améliorée pour corriger l'URL d'image
   */
  const fixImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // Nettoyer l'URL (supprimer espaces, etc.)
    const cleanUrl = imageUrl.trim();
    
    
    // Si l'URL commence par /api/uploads/, ajouter le serveur backend
    if (cleanUrl.startsWith('/api/uploads/')) {
      const correctedUrl = `http://localhost:8080${cleanUrl}`;
      return correctedUrl;
    }
    
    // Si l'URL pointe vers le serveur React par erreur, corriger
    if (cleanUrl.includes('localhost:3000/api/uploads/')) {
      const correctedUrl = cleanUrl.replace('localhost:3000/', 'localhost:8080/');
      return correctedUrl;
    }
    
    // Si c'est déjà une URL complète vers le bon serveur
    if (cleanUrl.startsWith('http://localhost:8080/api/uploads/')) {
      return cleanUrl;
    }
    
    // Si c'est juste un nom de fichier ou chemin relatif (sans / au début)
    if (!cleanUrl.startsWith('http') && !cleanUrl.startsWith('/')) {
      const correctedUrl = `http://localhost:8080/api/uploads/${cleanUrl}`;
      return correctedUrl;
    }
    
    // Pour les URLs externes ou autres formats
    if (cleanUrl.startsWith('http')) {
      return cleanUrl;
    }
    
    return cleanUrl;
  };

  /**
   * ✅ CORRECTION : Obtenir l'URL de l'image du produit avec toutes les variantes possibles
   */
  const getImageUrl = () => {
    if (!product) {
      return null;
    }
    
    // Essayer toutes les propriétés d'image possibles
    const rawImageUrl = product.imageUrl || 
                        product.image_url || 
                        product.image ||
                        product.imagePath ||
                        product.photo;
    
    if (!rawImageUrl) {
      return null;
    }
    
    return fixImageUrl(rawImageUrl);
  };

  /**
   * Dimensions selon la taille demandée
   */
  const getDimensions = () => {
    switch (size) {
      case 'small':
        return { width: 50, height: 50 };
      case 'medium':
        return { width: 80, height: 80 };
      case 'large':
        return { width: 120, height: 120 };
      case 'xl':
        return { width: 200, height: 200 };
      case 'card':
        return { width: '100%', height: '180px' };
      default:
        return { width: 80, height: 80 };
    }
  };

  const imageUrl = getImageUrl();
  const dimensions = getDimensions();
  const altText = alt || `Image de ${product?.name || product?.nom || 'produit'}`;

  // Style de base pour les images
  const baseStyle = {
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa',
    ...dimensions,
    ...style
  };

  // ✅ AMÉLIORATION : Gestion des erreurs et du loading
  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = (e) => {
    setImageLoading(false);
    setImageError(true);
    console.error('❌ Erreur chargement image:', imageUrl);
    console.error('❌ Détails erreur:', e.target.src);
    console.error('❌ Product:', product?.name || product?.nom);
  };

  // Si aucune image ou erreur de chargement
  if (!imageUrl || imageError) {
    if (!showPlaceholder) return null;
    
    return (
      <div 
        className={`d-flex align-items-center justify-content-center ${className}`}
        style={{
          ...baseStyle,
          backgroundColor: '#e9ecef',
          color: '#6c757d'
        }}
        title={`${imageError ? 'Erreur de chargement' : 'Aucune image'} pour ${product?.name || product?.nom || 'ce produit'}`}
      >
        {imageError ? (
          <AlertCircle size={dimensions.width * 0.3} />
        ) : (
          <ImageIcon size={dimensions.width * 0.3} />
        )}
      </div>
    );
  }

  return (
    <div className={`position-relative ${className}`} style={{ display: 'inline-block' }}>
      {/* Image principale */}
      <Image
        src={imageUrl}
        alt={altText}
        style={baseStyle}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="lazy" // ✅ AJOUT : Lazy loading pour les performances
      />
      
      {/* Indicateur de chargement */}
      {imageLoading && (
        <div 
          className="position-absolute top-0 start-0 d-flex align-items-center justify-content-center"
          style={{
            ...dimensions,
            backgroundColor: 'rgba(248, 249, 250, 0.8)',
            borderRadius: '8px'
          }}
        >
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Composant simplifié pour les listes de produits
 */
const ProductImageSmall = ({ product, className = '' }) => (
  <ProductImage 
    product={product} 
    size="small" 
    className={className}
  />
);

/**
 * Composant pour les cartes de produits
 */
const ProductImageCard = ({ product, className = '' }) => (
  <ProductImage 
    product={product} 
    size="card" 
    className={className}
  />
);

/**
 * Composant pour les détails de produit
 */
const ProductImageLarge = ({ product, className = '' }) => (
  <ProductImage 
    product={product} 
    size="large" 
    className={className}
  />
);

/**
 * Hook pour tester une URL d'image
 */
export const useImageTest = () => {
  const testImageUrl = async (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(true);
      };
      img.onerror = () => {
        console.error('❌ Test image échoué:', url);
        resolve(false);
      };
      img.src = url;
      
      // Timeout après 10 secondes
      setTimeout(() => {
        console.warn('⏰ Timeout test image:', url);
        resolve(false);
      }, 10000);
    });
  };

  return { testImageUrl };
};

/**
 * ✅ NOUVEAU : Fonction de debug pour analyser les images
 */
export const debugProductImage = (product) => {
  
};

export default ProductImage;